import cn from 'classnames';
import DOMPurify from 'dompurify';
import {
  useNavigate,
} from 'react-router-dom';
import { useMemo, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {API, ShareAPI} from '../../API';
import { Loader } from '../../components/Loader/Loader';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { ExpandableText } from '../../components/CollapsibleText/ExpandableText';
import { ampli } from "../../ampli";
import { MessageModal } from '../../components/MessagesTable/MessageModal';
import { MessagesContext } from './contexts/MessagesContext';
import { palette } from '../../utils/colors';

import styles from './NarrativePage.module.scss';
import {useCurrentUser} from "../../contexts/CurrentUser";
import messageStyles from '../../components/MessagesTable/ExtendedMessageView.module.scss';
import { StoriesBubbleChart } from '../../components/StoriesBubbleChart/StoriesBubbleChart';
import { SummaryPanel } from '../../components/SummaryPanel/SummaryPanel';
import { SentimentBar } from '../../components/SentimentBar/SentimentBar';

import { EvolutionChart } from '../../components/EvolutionChart/EvolutionChart';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { ImagesContainer } from '../../components/Image/ImagesContainer';
import { LanguagesDistribution } from '../../components/LanguagesDistribution/LanguagesDistribution';
import { TranslationSwitch } from '../../components/TranslationSwitch/TranslationSwitch';
import { MessagesAndLikesDonutChart } from '../../components/MessagesAndLikesDonutChart';
import { MessageTooltips } from '../../components/MessagesTable/MessageTooltips';
import { OriginalTextBlock } from '../../components/MessagesTable/OriginalTextBlock';
import { MessageInfoWrapper } from '../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../components/MessagesTable/MessageMetricsWrapper';

export const Distribution = ({ 
  narrative,
  isShare,
  sentimentStatus,
}) => {
  const [currentUser] = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const api = isShare ? ShareAPI : API;

  const {
    messages,
    originalSources,
    deduplication,
    narrativeShares,
    narrativeStats,
    narrativeSentimentStats,
    mostMentionedEntities,
    getMessages,
    fetchNarrativeStatsMessages,
    fetchNarrativeStatsStories,
    narrativeStatsStories,
    fetchNarrativeShares,
    fetchMostMentionedEntities,
    fetchOriginalSources,
    fetchNarrativeSetnimentStats,
    dispatch,
    translateMessage,
} = useContext(MessagesContext);

  const [messageModal, setMessageModal] = useState({message: null, isOpen: false, narrartiveMessage: null});
  const [messagesList, setMessagesList] = useState(null);

  const [sentimentData, setSentimentData] = useState(null);

  useEffect(() => {
    dispatch({ type: 'TOGGLE_TAB' });

    fetchOriginalSources(narrative, narrative.original_source_ids);
    fetchNarrativeSetnimentStats(narrative);
    
    if (narrative?.parameters?.create_stories) {
      fetchNarrativeStatsStories(narrative);
    }

    fetchNarrativeShares(narrative, null, null)
  }, [])

  useEffect(() => {
    if(sentimentData === null && narrativeSentimentStats) {
      setSentimentData(narrativeSentimentStats);
    }
  }, [narrativeSentimentStats])

  useEffect(() => {
    setSentimentData(null)
  },[sentimentStatus])

  useEffect(() => {
    ampli.track({
      event_type: 'Open Overview tab',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        narrative_id: narrative.id,
      }
    });
  }, []);

  const handleEvolutionChartChange = (aggregation, breakdown) => {
    fetchNarrativeShares(narrative, aggregation, breakdown);
  }

  const getThreatDescription = (threat) => {
    if (!threat?.is_public) {
      return threat?.description
    }

    let public_description;
    if(
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk'
    ) {
      public_description = threat?.public_description_uk;
    } else {
      public_description = threat?.public_description_en;
    }

    return public_description ?? threat?.description
  }

  let caseDescription = narrative?.is_threat ? getThreatDescription(narrative) : narrative?.description;
  const newText = caseDescription?.replace(/\n+(\s*\n+)?/g, '\n').split('\n');
  const sanitizedText = newText?.map(item => DOMPurify.sanitize(item, { ALLOWED_TAGS: ['mark', 'span'],  ALLOWED_ATTR: ['class']}));

  const clean = useMemo(
    // () => DOMPurify.sanitize(newText, { ALLOWED_TAGS: ['mark'] }),
    () => {
      return sanitizedText;
    },
    [sanitizedText],
  );

  useEffect(() => {
    if(originalSources) {
      setMessagesList(originalSources?.objects)
    } else if (messages?.objects?.length > 0) {
      setMessagesList(messages?.objects?.slice(0,1))
    }
  },[originalSources, messages])

  if(!narrative) {
    return <Loader />
  }


  const toggleTranslate = (messageId, isTranslation) => {
    setMessagesList(messagesList.map((message) => {
      if (message.id === messageId) {
        if (message.content_type === 'COMMENT' && message.parent_message) {
          return {
            ...message,
            isTranslation: isTranslation,
            parent_message: {
              ...message.parent_message,
              isTranslation: isTranslation
            }
          };
        } else {
        return {
          ...message,
          isTranslation: isTranslation,
          parent_message: {
            ...message.parent_message,
            isTranslation: isTranslation
          }
        }}
      } else {
        return message;
      }
  }))
  }

  const handleTranslation = (messageId, text, isTranslation) => {
    const target = messagesList.find((message) => message.id === messageId);
    const originalPostText =
      target?.content_type === 'COMMENT' ? target?.parent_message?.highlighted_text || target?.parent_message?.text : '';
    if (target.translated) {
      toggleTranslate(messageId, isTranslation);
    } else {
      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : narrative.parameters?.translate_keywords_query
        ? narrative.parameters?.keywords_query_origin_language
        : window.clientInformation.language.split('-', 1)[0];
      toggleTranslate(messageId, true);

      let newMessagesList = [...messagesList];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageId, originalPostText, nativeLanguage),
          translateMessage(messageId, text, nativeLanguage),
        ]).then((results) => {
          if (results[0].status === 'fulfilled') {
            const originalPostData = results[0].value;

            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                let m = {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: originalPostData.destination_text,
                  },
                };
                return m;
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  },
                };
              } else {
                return message;
              }
            });
          }

          if (results[1].status === 'fulfilled') {
            const messageData = results[1].value;
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: messageData.destination_text,
                };
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });
          }

          setMessagesList(newMessagesList);
        });
      } else {
        translateMessage(messageId, text, nativeLanguage)
          .then((data) => {
            setMessagesList(
              messagesList.map((message) => {
                if (message.id === messageId) {
                  return {
                    ...message,
                    isTranslation: true,
                    translated: data.destination_text,
                  };
                } else {
                  return message;
                }
              }),
            );
          })
          .catch((e) => {
            setMessagesList(
              messagesList.map((message) => {
                if (message.id === messageId) {
                  return {
                    ...message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                } else {
                  return message;
                }
              }),
            );
          });
      }

      ampli.track({
        event_type: 'Translate original source',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          narrative_id: narrative.id,
        },
      });
    }
  };

  const toggleTranslateChartMessage = (messageId, isTranslation, isStories) => {
    if(isStories === true) {
      dispatch({type: 'TOGGLE_TRANSLATE_STORIES_CHART_MESSAGES', payload: {messageId: messageId, isTranslation: isTranslation}})
    } else {
      dispatch({type: 'TOGGLE_TRANSLATE_CHART_MESSAGES', payload: {messageId: messageId, isTranslation: isTranslation}})
    }
  }

  const handleChartMessageTranslation = (messageId, text, isTranslation, isStories) => {
    let target;
    if(isStories === true) {
      target = narrativeStatsStories.objects.map(obj => obj.stories).flat().find(message => message.id === messageId);
    } else {
      target = narrativeStats.objects[0]?.messages?.find(message => message.id === messageId);
    }
    
    if (target.translated) {
      toggleTranslateChartMessage(messageId, isTranslation, isStories)
    } else {
      const nativeLanguage = currentUser?.translation_language ? currentUser?.translation_language : narrative.parameters?.translate_keywords_query ? 
        narrative.parameters?.keywords_query_origin_language : 
        window.clientInformation.language.split('-', 1)[0];
      toggleTranslateChartMessage(messageId, true, isStories);
      translateMessage(messageId, text, nativeLanguage).then(data => {
        if(isStories === true) {
          dispatch({type: 'TRANSLATE_STORIES_CHART_MESSAGE', payload: {data: data, messageId: messageId}})
        } else {
          dispatch({type: 'TRANSLATE_CHART_MESSAGE', payload: {data: data, messageId: messageId}})
        }
      })
      .catch(e => {
        if(isStories === true) {
          dispatch(
            {
              type: 'TRANSLATION_ERROR_STORIES_CHART_MESSAGE',
              payload: {data: t('We were not able to translate this text.'), messageId: messageId}}
          )
        } else {
          dispatch(
            {
              type: 'TRANSLATION_ERROR_CHART_MESSAGE',
              payload: {data: t('We were not able to translate this text.'), messageId: messageId}}
          )
        }
        
      })

      ampli.track({
        event_type: 'Translate circle chart message',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          narrative_id: narrative.id,
        }
      });
    }
  }

  let narrativeColor = null;
  if (narrativeShares?.objects) {
    narrativeColor = Object.fromEntries(
      narrativeShares.objects.map((narrative, i) => [narrative.id, palette[i % palette.length]])
    );
  }


  return (
    <div className="list-content">
      {caseDescription?.length > 0 ? (
        <div className="report-section">
          <h3>{t('Description')}</h3>
          <div className={styles.descriptionCard}>
            {clean.map((item, i) => (
              <p
                key={`paragraph_${i}`}
                dangerouslySetInnerHTML={{ __html: item }}
              ></p>
            ))}
          </div>
        </div>
      ) : (
        ''
      )}
      <div className="report-section">
        <h3>{t('Summary')}</h3>
        <SummaryPanel
          narrativeIds={[narrative.id]}
          isShare={isShare}
          report={narrative}
        />
      </div>

      {narrative.type !== 'IMPACT_ASSESSMENT' && messagesList ? (
        <div className="report-section">
          <h3>{t('Earliest publication in case')}</h3>
          <table
            className={`${messageStyles.extendedMessages} ${styles.extendedMessages} original-source`}
          >
            <tbody>
              {messagesList.map((message, index) => {
                let followers_cnt = message?.source?.audience;
                let textClass = styles.extendedMessage;
                let textClassParent = messageStyles.extendedMessage;

                if (
                  message.parent_message?.error &&
                  message.parent_message?.isTranslation
                ) {
                  textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`;
                } else if (message.error && !message.isTranslation) {
                  textClassParent = messageStyles.extendedMessage;
                }

                if (message.error && message.isTranslation) {
                  textClass = `${styles.extendedMessage} ${messageStyles.error}`;
                } else if (message.error && !message.isTranslation) {
                  textClass = styles.extendedMessage;
                }

                const textForTranslation = message.highlighted_text
                  ? replaceNewlinesWithBreaks(message.highlighted_text)
                  : replaceNewlinesWithBreaks(message.text);

                return (
                  <tr key={`message.message_url_${index}`}>
                    <td>
                      <MessageInfoWrapper
                        message={message}
                        followers_cnt={followers_cnt}
                        isShare={isShare}
                        currentUser={currentUser}
                        narrative={narrative}
                        narrartiveMessage={null}
                        setMessageModal={setMessageModal}
                      />
                      
                      <div
                        className={`${messageStyles.messageContainer} ${styles.messageContainer}`}
                      >
                        <MessageMetricsWrapper
                          message={message}
                          narrative={narrative}
                          isShare={isShare}
                          messageMenuOptions={[]}
                        />

                        {message.parent_message ? (
                          <OriginalTextBlock
                            message={message}
                            isShare={isShare}
                            currentUser={currentUser}
                            narrative={narrative}
                            textClassParent={textClassParent}
                          />
                        ) : (
                          ''
                        )}
                        
                        {message?.images && message?.images?.length > 0 ? (
                          <ImagesContainer
                            images={message?.images}
                            showCount={3}
                            className={styles.imagesContainer}
                          />
                        ) : (
                          ''
                        )}

                        <div>
                          {message.isTranslation === true &&
                          !message.translated ? (
                            <div>
                              <LoaderSmall />
                            </div>
                          ) : (
                            <ExpandableText
                              length={150}
                              highlights={['KEYWORDS']}
                              text={
                                message.isTranslation
                                  ? message.translated
                                  : message.highlighted_text || message.text
                              }
                              textClassName={textClass}
                              onExpand={(expanded) => {
                                if (expanded) {
                                  ampli.track({
                                    event_type: 'Expand original source',
                                    event_properties: {
                                      user_id: currentUser?.id,
                                      workspace_id: currentUser?.workspace_id,
                                      narrative_id: narrative.id,
                                    },
                                  });
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="message-controlls">
                      <div className={styles.translationWrapperNew}>
                        <TranslationSwitch
                          isTranslation={message.isTranslation}
                          theme="light"
                          size="medium"
                          handleTranslation={(bool) => {
                            handleTranslation(
                              message.id,
                              textForTranslation,
                              bool,
                            );
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        ''
      )}
      <MessageTooltips scope=".original-source"/>

      {narrative.type !== 'IMPACT_ASSESSMENT' ? (
        <EvolutionChart
          narrative={narrative}
          data={narrativeShares}
          onChange={(aggregation, breakdown) => {
            handleEvolutionChartChange(aggregation.value, breakdown.value);
          }}
          isShare={isShare}
        />
      ) : (
        ''
      )}

      <LanguagesDistribution narrative={narrative} isShare={isShare}/>

      <SentimentBar
        data={sentimentData}
        entity={narrative}
        entityType={narrative?.is_threat ? 'threat' : 'case'}
        isShare={isShare}
      />

      <div className={styles.breakdownsContainer}>
        <div className={styles.breakdownsRow}>
          <MessagesAndLikesDonutChart
            narrative={narrative}
            isShare={isShare}
            mode='publications'
            title={t('Publication distribution by platform')}
          />
          <MessagesAndLikesDonutChart
            narrative={narrative}
            isShare={isShare}
            mode='likes'
            title={t('Reaction distribution by platform')}
            colors={[
              '#6929C4',
              '#8A3FFC',
              '#A56EFF',
              '#BE95FF',
              '#D4BBFF',
              '#E8DAFF',
              '#F6F2FF',
              '#491D8B',

            ]}
          />
        </div>
      </div>


      {narrative?.type !== 'TOPIC_MODELING' &&
        (narrative?.parameters?.create_stories &&
        narrative?.population_status?.stories_calculation_status === 'DONE') ? (
        <div className="report-section">
          <h3 className="message-distribution-header">{t('Publication distribution by stories')}</h3>
          <div className="card card-circlechartwrapper">
            <div className="card-header" />
            <div className="card-body">
              {narrativeStatsStories ? (
                <StoriesBubbleChart
                  data={narrativeStatsStories}
                  onCircleClick={(messageID) => {
                    ampli.track({
                      event_type: 'Open bubble in opinions bubble-chart',
                      event_properties: {
                        user_id: currentUser?.id,
                        workspace_id: currentUser?.workspace_id,
                        narrative_id: narrative.id,
                      },
                    });
                  }}
                  narrativeColor={narrativeColor}
                  narrative={narrative}
                  isShare={isShare}
                  currentUser={currentUser}
                  onTranslationClick={(messageId, text, bool) =>
                    handleChartMessageTranslation(
                      messageId,
                      replaceNewlinesWithBreaks(text),
                      bool,
                      true,
                    )
                  }
                />
              ) : (
                <Loader />
              )}
            </div>
          </div>
        </div>
      ) : (
        ''
      )}

      {messageModal.isOpen ? (
        <MessageModal
          message={messageModal.message}
          isOpen={messageModal.isOpen}
          onChange={(bool, selected) => {
            setMessageModal({
              message: null,
              isOpen: bool,
              narrartiveMessage: null,
            });
            // setModalHighlights(selected)
          }}
          narrativeMessage={messageModal.narrartiveMessage}
          // selected={modalHighlights.length === 0 ? highlights : modalHighlights}
          isShare={isShare}
        />
      ) : (
        ''
      )}
    </div>
  );
}
